<template>
    <a-modal
        :visible="true"
        width="75vw"
        :footer="null"
        :closable="false"
        :destroy-on-close="true"
        centered
        wrap-class-name="apv-wrap"
        @cancel="$emit('close')"
    >
        <div class="apv-modal">
            <div class="apv-head">
                <div>
                    <h3>Adicionar produtos</h3>
                    <div class="apv-sub">Escolha itens da base padrão do Club ou cadastre um novo produto</div>
                </div>
                <a-tooltip title="Fechar">
                    <a-button class="apv-x" @click="$emit('close')">
                        <span class="material-icons-outlined">close</span>
                    </a-button>
                </a-tooltip>
            </div>

            <div class="apv-tools">
                <a-input
                    v-model="search"
                    class="apv-search"
                    placeholder="Buscar produto"
                    allow-clear
                    @change="onSearch"
                    @pressEnter="loadProducts(1)"
                >
                    <a-icon slot="prefix" type="search" />
                </a-input>
                <a-select
                    v-model="categoryId"
                    class="apv-search"
                    show-search
                    allowClear
                    placeholder="Categoria"
                    :options="categoryOptions"
                    :filterOption="filterOption"
                    @change="loadProducts(1)"
                />
                <a-checkbox v-model="top300" @change="loadProducts(1)">Apenas os 300+</a-checkbox>
                <a-button class="apv-tools-right" type="primary" @click="showNewProduct = true">
                    <a-icon type="plus" /> Novo produto
                </a-button>
            </div>

            <div class="apv-body">
                <a-table
                    size="small"
                    :columns="columns"
                    :data-source="products"
                    row-key="id"
                    :loading="loading"
                    :pagination="top300 ? { pageSize: 30, size: 'small' } : pagination"
                    :scroll="{ x: 'max-content' }"
                    :locale="{ emptyText: 'Nenhum produto encontrado.' }"
                    @change="(p) => !top300 && loadProducts(p.current)"
                >
                    <template slot="product" slot-scope="text, product">
                        <a-checkbox
                            :checked="!!product.saved"
                            :disabled="!!product.saved || !!product.adding"
                            @change="addProduct(product)"
                        >
                            <span class="apv-name">{{ String(product.name || "").toUpperCase() }}</span>
                        </a-checkbox>
                        <a-tag v-if="product.saved" color="green" class="apv-tag">Adicionado</a-tag>
                    </template>
                </a-table>
            </div>

            <div class="apv-foot">
                <a-button @click="$emit('close')">Fechar</a-button>
            </div>
        </div>

        <ProductFormV2Modal v-if="showNewProduct" @saved="$emit('add_product')" @close="showNewProduct = false" />
    </a-modal>
</template>

<script>
import QuotationService from "@/services/QuotationService";
import FoodService from "@/services/v3/foodService";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";
import ProductFormV2Modal from "./product-form-v2.modal.vue";

export default {
    name: "AddProductV2Modal",
    mixins: [loaderMixin],
    components: { ProductFormV2Modal },
    data() {
        return {
            service: new QuotationService(),
            foodService: new FoodService(),
            loading: false,
            products: [],
            categories: [],
            search: "",
            searchTimeout: null,
            categoryId: undefined,
            top300: false,
            showNewProduct: false,
            cancelSource: null,
            pagination: {
                current: 1,
                pageSize: 15,
                total: 0,
                showSizeChanger: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
            },
            columns: [
                { title: "Produto", key: "product", scopedSlots: { customRender: "product" } },
                { title: "Embalagem", dataIndex: "purchase_unit", key: "purchase_unit" },
                { title: "Código", dataIndex: "ean", key: "ean" },
            ],
        };
    },
    computed: {
        categoryOptions() {
            return this.categories.map((c) => ({ value: c.cat_id, label: c.cat_descricao }));
        },
    },
    created() {
        this.service
            .load_provider_default_categories()
            .then(({ data }) => {
                this.categories = data.categorias || [];
            })
            .catch(() => {});
        this.loadProducts(1);
    },
    beforeDestroy() {
        clearTimeout(this.searchTimeout);
        if (this.cancelSource) this.cancelSource.cancel();
    },
    methods: {
        filterOption(input, option) {
            const label = option.componentOptions.children[0].text || "";
            return label.toLowerCase().includes(input.toLowerCase());
        },
        onSearch() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => this.loadProducts(1), 500);
        },
        loadProducts(page = this.pagination.current) {
            clearTimeout(this.searchTimeout);
            if (this.cancelSource) this.cancelSource.cancel();
            this.cancelSource = axios.CancelToken.source();
            this.loading = true;
            const params = {
                fu: "",
                page,
                limit: this.top300 ? "300" : "",
                name: this.search,
                category_id: this.categoryId,
            };
            if (this.top300) {
                params.in_use = 1;
                params.partial_sort = 1;
                params.start_at = new Date(new Date().setMonth(new Date().getMonth() - 12));
            }
            this.foodService
                .list(params, this.cancelSource.token)
                .then(({ data }) => {
                    if (data.data) {
                        this.products = data.data;
                        this.pagination.total = (data.meta && data.meta.total) || 0;
                    } else {
                        this.products = (data || []).slice().sort((a, b) => (a.name > b.name ? 1 : -1));
                        this.pagination.total = this.products.length;
                    }
                    this.pagination.current = page;
                    this.loading = false;
                })
                .catch((error) => {
                    if (axios.isCancel(error)) return;
                    this.loading = false;
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        buildPayload(product) {
            return {
                cliente: this.service.getClientId(),
                pro_id: product.id,
                pro_descricao: product.name,
                pro_ean: product.ean,
                pro_observacao: product.pro_observacao,
                pro_embalagem_text: product.packing,
                pro_unidade_compra: product.purchase_unit,
                pro_generico: product.is_generic,
                categoria_select: { cat_id: 2 },
            };
        },
        addProduct(product) {
            if (product.saved || product.adding) return;
            this.$set(product, "adding", true);
            this.service
                .insert_client_product(this.buildPayload(product))
                .then(({ data }) => {
                    this.$set(product, "pro_id", data.produto);
                    this.$set(product, "saved", true);
                    this.$emit("add_product", product);
                })
                .catch((error) => {
                    const message = error.response && error.response.data && error.response.data.mensagem;
                    if (message === "Código já existe") {
                        this.$set(product, "pro_id", error.response.data.pro_id);
                        this.$set(product, "saved", true);
                        this.snack("Produto já está no seu catálogo", "error");
                        this.$emit("add_product", product);
                    } else if (message === "Erro com categoria") {
                        this.snack("Cliente não trabalha com essa categoria de produto.", "error");
                    } else {
                        ErrorHandlerService.handle(error, this.$store);
                    }
                })
                .finally(() => {
                    this.$set(product, "adding", false);
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.apv-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.apv-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.apv-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.apv-x.ant-btn,
.apv-x.ant-btn:focus,
.apv-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.apv-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.apv-tools {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 20px 12px;
}
.apv-search {
    flex: 1 1 220px;
    max-width: 320px;
}
.apv-tools-right {
    margin-left: auto;
}
.apv-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 20px 12px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.apv-name {
    font-weight: 600;
    color: #262626;
}
.apv-tag {
    margin-left: 8px;
}
.apv-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
</style>

<style lang="scss">
.apv-wrap {
    .ant-modal {
        min-width: 560px;
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
